<div class="step-side-form-container">
  <!-- ─── Loader (shown while CreateProfile + signIn run) ─── -->
  <div class="signup-form-container" *ngIf="loading">
    <header class="signup-logo">
      <img class="signup-logo__img" src="../../../../../assets/img/SyncedBlackLogo.svg" alt="Synced"/>
    </header>

    <section class="acct-loader" role="status" aria-live="polite">
      <div class="acct-loader__kicker">{{ loadingStepLabel }}</div>
      <h1 class="acct-loader__title">Setting up<br>your account.</h1>
      <p class="acct-loader__sub">
        This usually takes a few seconds. We'll let you know the moment your books are live.
      </p>
      <ol class="acct-loader__list">
        <li *ngFor="let phase of loadingPhases; let i = index"
            class="acct-loader__item"
            [class.acct-loader__item--done]="i < currentPhase"
            [class.acct-loader__item--active]="i === currentPhase"
            [class.acct-loader__item--pending]="i > currentPhase">
          <span class="acct-loader__indicator" aria-hidden="true">
            <svg class="acct-loader__check" *ngIf="i < currentPhase"
                 width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6.2l2.4 2.4L9.5 3.8" stroke="currentColor" stroke-width="1.8"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="acct-loader__dot" *ngIf="i === currentPhase"></span>
          </span>
          <div class="acct-loader__body">
            <div class="acct-loader__label">{{ phase.title }}</div>
            <div class="acct-loader__desc">{{ phase.desc }}</div>
            <div class="acct-loader__meta">
              <span *ngIf="i < currentPhase">DONE · {{ phase.meta }}</span>
              <span *ngIf="i === currentPhase"><span class="acct-loader__meta-dot"></span> RUNNING · {{ phase.meta }}</span>
            </div>
          </div>
        </li>
      </ol>
    </section>
  </div>

  <!-- ─── Signup form ─── -->
  <div class="signup-form-container" *ngIf="!loading">
    <header class="signup-logo">
      <img class="signup-logo__img" src="../../../../../assets/img/SyncedBlackLogo.svg" alt="Synced"/>
    </header>

    <form class="signup-form" [formGroup]="form" role="form" (ngSubmit)="submit()" novalidate>
      <header class="signup-form__heading">
        <h1 class="signup-form__title">Books that<br>balance themselves.</h1>
        <p class="signup-form__sub">
          Create your Synced account. No card, no commitment — just smarter bookkeeping in a few minutes.
        </p>
      </header>

      <div class="sso-grid sso-grid--single">
        <button type="button" class="sso-btn" (click)="signUpWithXero()">
          <img class="sso-btn__logo" src="../../../../../assets/img/company/XeroLogo.svg" alt="" aria-hidden="true"/>
          <span>Sign Up With Xero</span>
        </button>
      </div>

      <div class="sep" role="separator" aria-hidden="true">
        <span class="sep__label">or with email</span>
      </div>

      <div class="field-row">
        <div class="field field--half">
          <label class="field__label" for="firstname">First name <span class="field__required">*</span></label>
          <input id="firstname" class="field__input" formControlName="firstname" autocomplete="given-name"
                 placeholder="Ada"
                 [class.field__input--invalid]="f.firstname.invalid && (f.firstname.touched || isWarningShown)">
          <span class="field__error"
                *ngIf="f.firstname.errors?.required && isWarningShown && (f.firstname.dirty || f.firstname.touched)">
            First name is required
          </span>
        </div>
        <div class="field field--half">
          <label class="field__label" for="lastname">Last name <span class="field__required">*</span></label>
          <input id="lastname" class="field__input" formControlName="lastname" autocomplete="family-name"
                 placeholder="Lovelace"
                 [class.field__input--invalid]="f.lastname.invalid && (f.lastname.touched || isWarningShown)">
          <span class="field__error"
                *ngIf="f.lastname.errors?.required && isWarningShown && (f.lastname.dirty || f.lastname.touched)">
            Last name is required
          </span>
        </div>
      </div>

      <div class="field">
        <label class="field__label" for="companyname">Company <span class="field__required">*</span></label>
        <input id="companyname" class="field__input" formControlName="companyname" autocomplete="organization"
               placeholder="Analytical Engines Ltd."
               [class.field__input--invalid]="f.companyname.invalid && (f.companyname.touched || isWarningShown)">
        <span class="field__error"
              *ngIf="f.companyname.errors?.required && isWarningShown && (f.companyname.dirty || f.companyname.touched)">
          Company is required
        </span>
      </div>

      <div class="field">
        <label class="field__label" for="country">Country <span class="field__required">*</span></label>
        <ng-select id="country" formControlName="country" [items]="countryList" bindLabel="name" bindValue="name"
                   [groupBy]="countryGroup" placeholder="Select country" [clearable]="true"></ng-select>
        <span class="field__error"
              *ngIf="f.country.errors?.required && isWarningShown && (f.country.dirty || f.country.touched)">
          Country is required
        </span>
      </div>

      <div class="field">
        <label class="field__label" for="email">Work email <span class="field__required">*</span></label>
        <input id="email" class="field__input" formControlName="email" autocomplete="email"
               inputmode="email" type="email"
               placeholder="ada@company.com"
               [class.field__input--invalid]="f.email.invalid && (f.email.touched || isWarningShown)">
        <span class="field__error"
              *ngIf="f.email.errors?.required && isWarningShown && (f.email.dirty || f.email.touched)">
          Work email is required
        </span>
        <span class="field__error"
              *ngIf="!f.email.errors?.required && f.email.errors && isWarningShown && (f.email.dirty || f.email.touched) && !f.email.errors?.emailIsBusy">
          Please enter a valid email
        </span>
        <span class="field__error"
              *ngIf="f.email.errors?.emailIsBusy && (f.email.dirty || f.email.touched)">
          This email is already registered
        </span>
      </div>

      <div class="field">
        <label class="field__label" for="password">Password <span class="field__required">*</span></label>
        <div class="field__input-wrap">
          <input id="password" class="field__input field__input--with-toggle"
                 [type]="showPassword ? 'text' : 'password'"
                 formControlName="password" autocomplete="new-password"
                 placeholder="At least 8 characters"
                 (focus)="passwordFocused = true"
                 (blur)="passwordFocused = false"
                 [class.field__input--invalid]="f.password.invalid && (f.password.touched || isWarningShown)">
          <button type="button" class="field__toggle"
                  [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
                  (click)="showPassword = !showPassword">
            <svg *ngIf="!showPassword" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M1.5 10s3-6 8.5-6 8.5 6 8.5 6-3 6-8.5 6-8.5-6-8.5-6z" stroke="currentColor" stroke-width="1.5"/>
              <circle cx="10" cy="10" r="2.5" stroke="currentColor" stroke-width="1.5"/>
            </svg>
            <svg *ngIf="showPassword" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 3l14 14M8 5.2c.6-.1 1.3-.2 2-.2 5.5 0 8.5 6 8.5 6a15 15 0 01-2.3 2.9M12.5 12.5a3 3 0 01-4.3-4.3M5.1 5.1C2.9 6.5 1.5 10 1.5 10S4.5 16 10 16c1.4 0 2.7-.4 3.8-1"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="pwd-hints" aria-live="polite" *ngIf="showPwdHints">
          <span class="pwd-hints__pill" [class.is-ok]="pwdHasLength">
            <span class="pwd-hints__dot"></span>8+ characters
          </span>
          <span class="pwd-hints__pill" [class.is-ok]="pwdHasUppercase">
            <span class="pwd-hints__dot"></span>Uppercase
          </span>
          <span class="pwd-hints__pill" [class.is-ok]="pwdHasNumber">
            <span class="pwd-hints__dot"></span>Number
          </span>
          <span class="pwd-hints__pill" [class.is-ok]="pwdHasSymbol">
            <span class="pwd-hints__dot"></span>Symbol
          </span>
        </div>
        <span class="field__error"
              *ngIf="f.password.errors?.required && isWarningShown && (f.password.dirty || f.password.touched)">
          Password is required
        </span>
      </div>

      <label class="terms">
        <input type="checkbox" class="terms__input" formControlName="accountRemember">
        <span class="terms__text">
          I agree to the <a href="#" onclick="return false">Terms</a>
          and <a href="#" onclick="return false">Privacy Policy</a>.
        </span>
      </label>
      <span class="field__error field__error--standalone"
            *ngIf="f.accountRemember.errors?.required && isWarningShown">
        Please accept the Terms and Privacy Policy
      </span>

      <!-- SD-1165 Cloudflare Turnstile container. Rendered explicitly in
           ngAfterViewInit so it mounts reliably after the SPA route loads. -->
      <div #turnstileContainer class="cf-turnstile-host"></div>
      <span class="field__error field__error--standalone"
            *ngIf="turnstileBlocked">
        Verification couldn't load. Please disable any ad blocker or refresh the page.
      </span>
      <span class="field__error field__error--standalone"
            *ngIf="turnstileError && !turnstileBlocked && isWarningShown">
        Please complete the verification challenge above
      </span>

      <button type="submit" class="primary-btn"
              [class.primary-btn--disabled]="form.invalid || loading || !turnstileToken"
              [disabled]="loading">
        Create account <span aria-hidden="true">→</span>
      </button>

      <p class="signup-form__footer">
        Already have an account?
        <a [routerLink]="['/auth/login']">Log in</a>
      </p>
    </form>
  </div>
</div>

<!-- Yellow illustration column. Static mockup hero; no functional content. -->
<div class="step_side_image_container" aria-hidden="true">
  <div class="hero-card" *ngIf="!loading">
    <div class="hero-card__chip">No credit card required</div>
    <div class="hero-card__top">
      <span class="hero-card__kicker">MONTHLY CLOSE</span>
      <span class="hero-card__status">Reconciled</span>
    </div>
    <h2 class="hero-card__title">April 2026</h2>
    <ul class="hero-card__lines">
      <li><span>Stripe payout</span><span class="pos">+$12,480.00</span></li>
      <li><span>AWS · Infra</span><span>−$847.22</span></li>
      <li><span>Figma subscription</span><span>−$45.00</span></li>
      <li><span>Client retainer · Acme</span><span class="pos">+$8,500.00</span></li>
      <li><span>Payroll · April</span><span>−$24,300.00</span></li>
    </ul>
    <div class="hero-card__foot">
      <span class="hero-card__foot-label">AI CATEGORIZED</span>
      <span class="hero-card__foot-total">−$4,212.22</span>
    </div>
  </div>

  <div class="hero-books" *ngIf="loading">
    <div class="hero-books__badges">
      <span class="hero-books__badge hero-books__badge--dark">+ Almost ready</span>
      <span class="hero-books__badge">✓ Workspace</span>
      <span class="hero-books__badge">✓ Account</span>
      <span class="hero-books__badge">◉ Settings</span>
    </div>
    <div class="hero-books__card">
      <div class="hero-books__card-head">
        <span>Your books</span>
        <span>APR 2026</span>
      </div>
      <div class="hero-books__bar"></div>
      <div class="hero-books__bar hero-books__bar--short"></div>
      <div class="hero-books__bar"></div>
    </div>
  </div>
</div>